import { configureEcho } from '@laravel/echo-vue'
import * as Sentry from '@sentry/vue'
import '@vuepic/vue-datepicker/dist/main.css'
import { createNotivue } from 'notivue'
import 'notivue/animations.css'
import 'notivue/notification.css'
import { createPinia } from 'pinia'
import 'leaflet/dist/leaflet.css'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/themes/light-border.css'
import { createApp } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import VueTippy from 'vue-tippy'
import VueViewer from 'v-viewer'
import 'viewerjs/dist/viewer.css'

import App from '@/app/App.vue'
import { createEchoConfig } from '@/app/createEchoConfig'
import '@/app/styles/main.css'
import '@/assets/vue-datepicker.css'
import { useApiClient } from '@/composables/useApiClient'
import { useUi } from '@/composables/useUi'
import { applyIosFixes } from '@/iosFixes/iosFixes'
import { useMiddleware } from '@/middleware/useMiddleware'
import { router } from '@/router'
import { useUiStore } from '@/stores/ui'

const notivue = createNotivue({
  position: 'bottom-right',
})

const { middlewareMethods } = useMiddleware(router)

const app = createApp(App)
const pinia = createPinia()
const sentryDsn = import.meta.env.VITE_SENTRY_DSN

if (!import.meta.env.DEV && sentryDsn) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
    ],
    tracesSampleRate: 0.1,
  })
}

const echoConfig = createEchoConfig(import.meta.env)

if ('warning' in echoConfig) {
  if (import.meta.env.DEV) {
    console.warn(echoConfig.warning)
  }

  configureEcho(echoConfig.config)
}
else {
  configureEcho({
    ...echoConfig.config,
    authorizer: (channel) => {
      const { apiClient } = useApiClient()

      return {
        authorize: async (
          socketId,
          callback,
        ) => {
          const rsp = await apiClient.post(
            '/api/broadcasting/auth',
            {
              socket_id: socketId,
              channel_name: channel.name,
            },
          )

          callback(null, rsp.data)
        },
      }
    },
  })
}

middlewareMethods.forEach((middlewareMethod) => {
  router.beforeEach(middlewareMethod)
})

app.use(VueTippy, {
  defaultProps: {
    touch: false,
    zIndex: 40,
  },
})
app.use(VueViewer)
app.use(pinia)
app.use(notivue)
app.use(router)
app.component('ApexChart', VueApexCharts)
app.component('apexchart', VueApexCharts)

const { uiActions } = useUi()
const { storeAddToActionsInProgress } = useUiStore()
storeAddToActionsInProgress(uiActions.bootingApp)

if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
  applyIosFixes()
}

app.mount('#app')
